// One palette for all SERP matrices, report maps and Telegram images.
import { SERP_CLASSIFICATION_LABELS } from './serp-classification.js';
export const SERP_COLOR_LABELS = SERP_CLASSIFICATION_LABELS;
export type SerpColorKey = keyof typeof SERP_COLOR_LABELS;
export type SerpColors = Record<SerpColorKey, string>;
export const DEFAULT_SERP_COLORS: SerpColors = {
  owned: '#8BD4A6',
  donor: '#8BC5FA',
  fake: '#EF8377',
  competitor: '#B5A1F5',
  external: '#A2A1A9',
  about: '#6FCFC8',
  namesake: '#A2A1A9',
  unrelated: '#C2C5CC',
  unreviewed: '#E8B970',
  unknown: '#A2A1A9',
  conflict: '#EF8377',
};
export type LegacySerpColors = Omit<SerpColors, 'about' | 'namesake' | 'unrelated'>;
export function resolveSerpColors(colors?: SerpColors | LegacySerpColors | null): SerpColors {
  if (colors == null) return { ...DEFAULT_SERP_COLORS };
  const keys = Object.keys(colors);
  const legacyKeys = [
    'owned',
    'donor',
    'fake',
    'competitor',
    'external',
    'unreviewed',
    'unknown',
    'conflict',
  ];
  const legacy = keys.length === legacyKeys.length && legacyKeys.every((key) => keys.includes(key));
  const expected = legacy ? legacyKeys : Object.keys(DEFAULT_SERP_COLORS);
  if (
    keys.length !== expected.length ||
    expected.some((key) => !keys.includes(key)) ||
    Object.values(colors).some(
      (color) => typeof color !== 'string' || !/^#[\da-f]{6}$/i.test(color),
    )
  )
    throw new Error('Некорректная палитра карты выдачи');
  return { ...DEFAULT_SERP_COLORS, ...colors };
}
// Solid text on muted backgrounds; custom colors remain readable even when very pale/dark.
export function mixColor(color: string, background: string, strength: number) {
  const channel = (hex: string, offset: number) => parseInt(hex.slice(offset, offset + 2), 16);
  return (
    '#' +
    [1, 3, 5]
      .map((i) =>
        Math.round(channel(color, i) * strength + channel(background, i) * (1 - strength))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}
export function colorInk(color: string) {
  const rgb = [1, 3, 5].map((i) => {
    const c = parseInt(color.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return rgb[0]! * 0.2126 + rgb[1]! * 0.7152 + rgb[2]! * 0.0722 > 0.179 ? '#000000' : '#FFFFFF';
}
export const SERP_MAP_VIEWS = ['bricks', 'groups', 'queries'] as const;
export type SerpMapView = (typeof SERP_MAP_VIEWS)[number];
export const SERP_MAP_STYLES = ['risk', 'soft', 'share'] as const;
export type SerpMapStyle = (typeof SERP_MAP_STYLES)[number];
export const DEFAULT_SERP_MAP_STYLE: SerpMapStyle = 'risk';
export const SERP_MAP_STYLE_LABELS: Record<SerpMapStyle, string> = {
  risk: 'Акценты на рисках',
  soft: 'Тихий зелёный',
  share: 'Доля наших + риски',
};
export const SERP_MAP_STYLE_DESCRIPTIONS: Record<SerpMapStyle, string> = {
  risk: 'Спокойный фон; фейки и конкуренты выделены цветом.',
  soft: 'Приглушённый зелёный для наших; неразобранное — отдельным индикатором, подтверждённые риски — цветом.',
  share: 'Полоска доли наших позиций и отдельный сигнал проблемы.',
};
export function resolveSerpMapStyle(value: unknown): SerpMapStyle {
  if (value === undefined) return DEFAULT_SERP_MAP_STYLE;
  if (!SERP_MAP_STYLES.includes(value as SerpMapStyle))
    throw new Error('Неизвестное оформление плиток');
  return value as SerpMapStyle;
}
